/**
 * Category-level hero photographs for service pillar pages (TH + EN).
 *
 * Guarantees every service page ships a real, content-matching visual and a
 * unique og:image / twitter:image instead of the site-wide fallback.
 * A pillar that declares its own `heroImage` always wins.
 */
import notary from "@/assets/insights/notary.jpg";
import translation from "@/assets/insights/translation.jpg";
import legalization from "@/assets/insights/legalization.jpg";
import visaMarriage from "@/assets/insights/visa.jpg";
import background from "@/assets/services/background-check.jpg";

export type ServiceHeroCategory =
  | "notary"
  | "translation"
  | "legalization"
  | "background"
  | "visa-marriage"
  | "marriage"
  | "visa";

export interface ServiceHero {
  src: string;
  altTh: string;
  altEn: string;
  width: number;
  height: number;
}

const W = 1200;
const H = 630;

const NOTARY: ServiceHero = {
  src: notary,
  altTh:
    "โต๊ะทำงานทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) พร้อมตราประทับรับรองเอกสาร",
  altEn:
    "Notarial Services Attorney desk in Thailand with official documents and a notary stamp",
  width: W,
  height: H,
};

const TRANSLATION: ServiceHero = {
  src: translation,
  altTh: "โต๊ะทำงานนักแปลเอกสารรับรอง เปรียบเทียบเอกสารไทยกับคำแปลภาษาอังกฤษ",
  altEn:
    "Certified translator workspace comparing a Thai document with its English translation",
  width: W,
  height: H,
};

const LEGALIZATION: ServiceHero = {
  src: legalization,
  altTh:
    "เคาน์เตอร์บริการรับรองนิติกรณ์เอกสาร พร้อมแฟ้มเอกสารรอยื่นกรมการกงสุล กระทรวงการต่างประเทศ",
  altEn:
    "Consular legalization counter with document folders awaiting Thai MFA certification",
  width: W,
  height: H,
};

const BACKGROUND: ServiceHero = {
  src: background,
  altTh:
    "หนังสือรับรองความประพฤติ (Police Clearance Certificate) จากสำนักงานตำรวจแห่งชาติ พร้อมหนังสือเดินทางไทยและแบบพิมพ์ลายนิ้วมือ",
  altEn:
    "Royal Thai Police clearance certificate with a Thai passport and fingerprint form on a desk",
  width: W,
  height: H,
};

const VISA_MARRIAGE: ServiceHero = {
  src: visaMarriage,
  altTh:
    "หนังสือเดินทางและแบบฟอร์มคำขอวีซ่าบนโต๊ะที่ปรึกษาด้านวีซ่า ใบอนุญาตทำงาน และจดทะเบียนสมรส",
  altEn:
    "Passport and visa application forms on a Thailand visa, work permit and marriage consultant's desk",
  width: W,
  height: H,
};

export const SERVICE_HERO_IMAGES: Record<ServiceHeroCategory, ServiceHero> = {
  notary: NOTARY,
  translation: TRANSLATION,
  legalization: LEGALIZATION,
  background: BACKGROUND,
  "visa-marriage": VISA_MARRIAGE,
  marriage: VISA_MARRIAGE,
  visa: VISA_MARRIAGE,
};

export function getServiceHero(category: string): ServiceHero {
  return SERVICE_HERO_IMAGES[category as ServiceHeroCategory] ?? NOTARY;
}

/** Absolute URL for og:image / twitter:image (hashed asset path + site origin). */
export function serviceHeroAbsolute(category: string, siteUrl: string): string {
  const src = getServiceHero(category).src;
  return src.startsWith("http") ? src : `${siteUrl}${src}`;
}
